import React, { useEffect, useRef, useMemo, useContext, useState } from "react"
import styled from "styled-components"
import gsap from "gsap"

import colors from "assets/styles/colors"

import { isBrowser, vwToPx } from "utils/functions"

import Grid from "components/uIElements/BGGrid"
import VideoPlayer from "components/VideoPlayer"
import VideoPlayerResponsive from "components/VideoPlayerResponsive"
import { ScreenContext } from "components/Providers"

import video1 from "assets/videos/homepage/founder_stories__what_has_it_been_like.mp4"
import video2 from "assets/videos/homepage/how_do_you_define_a_successful.mp4"
import video3 from "assets/videos/homepage/what_is_is_like_to_be.mp4"
import POSTER1 from "assets/images/homepage/poster1.webp"
import POSTER2 from "assets/images/homepage/poster2.webp"
import POSTER3 from "assets/images/homepage/poster3.webp"
import StickyTarget from "utils/StickyTarget"

const videoData = [
  {
    poster: POSTER2,
    video: video2,
    name: "How do you define a successful relationship with a VC?",
  },
  {
    poster: POSTER3,
    video: video1,
    name: "What has it been like working with RRE?",
  },
  {
    poster: POSTER1,
    video: video3,
    name: "What is it like to be an entrepreneur?",
  },
]

const Videos: React.FC = () => {
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const scrollTl = useRef(gsap.timeline())
  const [fullscreen, setFullscreen] = useState<false | number>(false)
  const { tablet, mobile } = useContext(ScreenContext)
  const gridConfig = {
    sectionHeight: "100%",
    sectionRef: wrapper,
    animationClass: "video",
  }

  useEffect(() => {
    const tl = gsap
      .timeline({
        scrollTrigger: {
          trigger: "#Videos",
          scroller: "#___gatsby",
          start: "top top",
          end: "bottom bottom",
          scrub: true,
        },
      })
      .to(
        innerRef.current,
        {
          x: `-${vwToPx(100) * (videoData.length - 1)}px`,
          ease: "none",
        },
        0
      )

    scrollTl.current = tl

    return () => {
      tl.kill()
      scrollTl.current.kill()
    }
  }, [])

  const scrollTo = (index: number) => {
    const offset = index * window.innerHeight

    window.locomotiveScroll.scrollTo(wrapper ?? "#Videos", {
      disableLerp: true,
      offset,
      callback: () => {
        const video = document.getElementById(
          `video-${index}`
        ) as HTMLVideoElement
        setFullscreen(index)
        video?.play()
      },
    })
  }

  const videos = useMemo(
    () =>
      videoData.map((item, index, arr) => {
        if (!mobile && !tablet) {
          return (
            <VideoPlayer
              key={index}
              poster={item.poster}
              video={item.video}
              index={index}
              total={arr.length}
              scrollTo={scrollTo}
              fullscreen={fullscreen === index}
              name={item.name}
              title={""}
              setFullscreen={setFullscreen}
            />
          )
        } else {
          return (
            <VideoPlayerResponsive
              key={index}
              poster={item.poster}
              video={item.video}
              index={index}
              total={arr.length}
              scrollTo={scrollTo}
              name={item.name}
              title={""}
              globalFullScreen={fullscreen}
              setGlobalFullScreen={setFullscreen}
            />
          )
        }
      }),
    [videoData, mobile, tablet, fullscreen, setFullscreen]
  )

  return (
    <Wrapper
      ref={ref => setWrapper(ref)}
      data-scroll
      data-scroll-section
      $length={videoData.length}
    >
      <StickyTarget id="Videos" />
      {fullscreen === false && <Grid lineConfig={gridConfig} />}

      <Inner
        data-scroll
        data-scroll-sticky
        data-scroll-target={"#Videos"}
        $length={videoData.length}
      >
        <InnerInner $length={videoData.length} ref={innerRef}>
          {videos}
        </InnerInner>
      </Inner>
    </Wrapper>
  )
}

export default Videos

const Wrapper = styled.section<{ $length: number }>`
  will-change: transform;
  background-color: ${colors.black800};
  position: relative;
  height: ${props => {
    return isBrowser()
      ? `${window.innerHeight * props.$length}px`
      : `${100 * props.$length}vh`
  }};
  width: 100%;
`

const Inner = styled.div<{ $length: number }>`
  position: relative;

  height: ${isBrowser() ? `${window.innerHeight}px` : "100vh"};
`

const InnerInner = styled.div<{ $length: number }>`
  display: flex;
  width: ${props => "calc(100vw * " + props.$length + ")"};
  height: 100%;
  transform: translate3d(0, 0, 0);
  will-change: transform;
`
